import React, { useCallback, useEffect, useState } from 'react'
import { CalendarEvent, google, ics, outlook, yahoo } from 'calendar-link'
import { DateTime, Duration } from 'luxon'
import { When } from 'react-if'
import { Button } from '@/atoms/Button'
import { ExternalLink } from '@/atoms/ExternalLink'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { CheckBadgeIcon } from '@/atoms/Icons/CheckBadge'
import { UserAddIcon } from '@/atoms/Icons/UserAddIcon'
import { ParagraphLG, HeadingXL, LabelXS, TitleMD, CaptionSM, TitleLG } from '@/atoms/Text'
import { LivestreamRsvp } from '@/organisms/Livestream/LivestreamCountdown/LivestreamCountdown'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'

interface LocalizedCountdownProps {
  onHandleRsvp?: () => void
  onAddToCalendar?: () => void
  startAt: string
  hasRsvp?: boolean
  rsvpComplete?: boolean
  showCalendar?: boolean
  event?: CalendarEvent
  livestreamRsvpList?: LivestreamRsvp[]
  streamId?: string
}

function defaultToPluralWhileLoading(value?: number): number {
  return value ?? 2
}

export const LocalizedCountdown: React.FC<LocalizedCountdownProps> = ({
  onHandleRsvp,
  onAddToCalendar,
  startAt,
  rsvpComplete,
  hasRsvp,
  showCalendar,
  event,
  livestreamRsvpList,
  streamId,
}) => {
  const [completedCalendarAdd, setCompletedCalendarAdd] = useState(false)
  const [duration, setDuration] = useState<Duration>(
    DateTime.fromISO(startAt).diffNow().shiftTo('days', 'hours', 'minutes'),
  )
  useEffect(() => {
    const interval = setInterval(() => {
      setDuration(DateTime.fromISO(startAt).diffNow().shiftTo('days', 'hours', 'minutes'))
    }, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [startAt])

  const containerClass =
    'flex flex-col gap-4 items-center text-white rounded-lg shadow-lg bg-core-gray-950/60 backdrop-blur-md py-4 px-10 md:px-12'
  const headingClass = 'flex flex-row gap-8 items-center'
  const timeClass = 'flex flex-col items-center justify-content-center'

  const { t } = useTranslate('livestream')
  const { locale = 'en' } = useLocale()
  const formatter = Intl.NumberFormat(locale, { minimumIntegerDigits: 2, maximumFractionDigits: 0 })
  const livestreamLocalDate = DateTime.fromISO(startAt).toLocaleString(DateTime.DATETIME_FULL)

  const hasCalendarComplete =
    livestreamRsvpList &&
    livestreamRsvpList.some((livestream: LivestreamRsvp) => livestream.id === streamId && livestream.calendarConfirm)

  const handleCalendarAdd = useCallback(() => {
    if (livestreamRsvpList) {
      livestreamRsvpList.map((livestream) => {
        if (livestream.id === streamId) {
          livestream.calendarConfirm = true
        }
      })
      localStorage.setItem('livestreamRsvp', JSON.stringify(livestreamRsvpList))
    }
    setCompletedCalendarAdd(true)
    setTimeout(() => {
      setCompletedCalendarAdd(false)
    }, 3000)
  }, [livestreamRsvpList, streamId])

  return (
    <div className={containerClass}>
      <When condition={!showCalendar && hasRsvp && !rsvpComplete && !hasCalendarComplete}>
        <div className="flex flex-row items-center gap-1">
          <CheckBadgeIcon color="gray-100" size={16} />
          <LabelXS className="lg:text-xs">{t('rsvpComplete', 'RSVP Complete')}</LabelXS>
        </div>
      </When>
      <When condition={hasCalendarComplete && completedCalendarAdd}>
        <div className="flex flex-row items-center gap-1">
          <CheckBadgeIcon color="gray-100" size={16} />
          <LabelXS className="lg:text-xs">{t('addedToCalendar', 'Added to Calendar')}</LabelXS>
        </div>
      </When>
      <When condition={(!showCalendar && !rsvpComplete) || hasCalendarComplete}>
        <div className={headingClass}>
          <div className={timeClass}>
            <HeadingXL weight="bold">
              {typeof duration?.days !== 'undefined' ? formatter.format(duration.days) : '--'}
            </HeadingXL>
            <ParagraphLG>
              {t('day', {
                count: defaultToPluralWhileLoading(duration?.days),
                defaultValue: 'Day',
                defaultValue_other: 'Days',
              })}
            </ParagraphLG>
          </div>

          <div className={timeClass}>
            <HeadingXL weight="bold">
              {typeof duration?.hours !== 'undefined' ? formatter.format(duration?.hours) : '--'}
            </HeadingXL>
            <ParagraphLG>
              {t('hour', {
                count: defaultToPluralWhileLoading(duration?.hours),
                defaultValue: 'Hour',
                defaultValue_other: 'Hours',
              })}
            </ParagraphLG>
          </div>

          <div className={timeClass}>
            <HeadingXL weight="bold">
              {typeof duration?.minutes !== 'undefined' ? formatter.format(duration?.minutes) : '--'}
            </HeadingXL>
            <ParagraphLG>{t('min', 'Min.')}</ParagraphLG>
          </div>
        </div>
        <When condition={hasRsvp && !hasCalendarComplete}>
          <Button className="gap-1 rounded-lg border-0 text-gray-950" onClick={onAddToCalendar} variant="lightgray">
            <CalendarIcon color="gray-950" size={16} />
            <LabelXS className="lg:text-xs" weight="semibold">
              {t('addToCalendar', 'Add to Calendar')}
            </LabelXS>
          </Button>
        </When>
        <When condition={!hasRsvp}>
          <Button className="gap-1 rounded-lg border-0 text-gray-950" onClick={onHandleRsvp} variant="lightgray">
            <UserAddIcon color="gray-950" size={16} />
            <LabelXS className="lg:text-xs" weight="semibold">
              {t('rsvpLivestream', 'RSVP Livestream')}
            </LabelXS>
          </Button>
        </When>
      </When>
      <When condition={rsvpComplete}>
        <div className="flex flex-col items-center gap-4 py-4">
          <CheckBadgeIcon color="gray-100" size={27} />
          <TitleMD weight="bold">{t('rsvpComplete', 'RSVP Complete')}!</TitleMD>
          <CaptionSM className="text-gray-400">
            {t('livestreamOnDate', `Livestream on {{livestreamLocalDate}} `, { livestreamLocalDate })}
          </CaptionSM>
        </div>
      </When>
      <When condition={showCalendar && !rsvpComplete && !hasCalendarComplete}>
        <div className="flex flex-col items-center gap-4 py-2">
          <CalendarIcon color="gray-100" size={20} />
          <TitleLG weight="bold">{t('addToCalendar', 'Add to Calendar')}</TitleLG>
          {event && (
            <ul className="photon-caption-sm flex flex-row gap-4 font-[350] text-gray-400">
              <li className="hover:text-gray-200">
                <ExternalLink href={google(event)}>Google</ExternalLink>
              </li>
              <li className="hover:text-gray-200">
                <ExternalLink href={ics(event)}>iCalendar</ExternalLink>
              </li>
              <li className="hover:text-gray-200">
                <ExternalLink href={outlook(event)}>Outlook</ExternalLink>
              </li>
              <li className="hover:text-gray-200">
                <ExternalLink href={yahoo(event)}>Yahoo</ExternalLink>
              </li>
            </ul>
          )}
          <Button
            className="mt-4 gap-1 rounded-lg border-0 text-gray-100 hover:bg-gray-500"
            onClick={handleCalendarAdd}
            variant="gray-600"
          >
            <LabelXS className="lg:text-xs">{t('done', 'Done')}</LabelXS>
          </Button>
        </div>
      </When>
    </div>
  )
}
